import React from "react";
import { Button, Stack, Typography } from "@mui/material";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import DownloadIcon from "@mui/icons-material/Download";
import PaymentRoundedIcon from "@mui/icons-material/PaymentRounded";
import { useGetInvoicesQuery, usePayInvoiceMutation } from "../../../Api/platformApi";
import { API_URL } from "../../../config";
import {
  AppPage,
  PageHeader,
  DataTable,
  StatusBadge,
  type DataTableColumn,
} from "../../../Components/ui";
import { NAVY } from "../../../theme/tokens";

const money = (v: any) => "$" + Number(v ?? 0).toFixed(2);

const Invoices: React.FC = () => {
  const { data, isLoading } = useGetInvoicesQuery();
  const [pay] = usePayInvoiceMutation();
  const rows = data?.data ?? [];

  const downloadPdf = async (id: number, number: string) => {
    const res = await fetch(`${API_URL}/platform/billing/invoices/${id}/pdf`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
        "X-Company-Id": localStorage.getItem("intalk_company_id") || "",
      },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${number}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns: DataTableColumn<any>[] = [
    {
      id: "invoice_number",
      label: "Invoice",
      render: (inv) => (
        <Typography fontSize={13} fontWeight={700} color={NAVY}>
          {inv.invoice_number}
        </Typography>
      ),
    },
    {
      id: "created_at",
      label: "Date",
      render: (inv) => new Date(inv.created_at).toLocaleDateString(),
    },
    {
      id: "total",
      label: "Total",
      align: "right",
      render: (inv) => (
        <Typography fontSize={13} fontWeight={600} color={NAVY}>
          {money(inv.total)}
        </Typography>
      ),
    },
    {
      id: "balance_due",
      label: "Balance Due",
      align: "right",
      render: (inv) => (
        <Typography fontSize={13} fontWeight={600}>
          {money(inv.balance_due)}
        </Typography>
      ),
    },
    {
      id: "status",
      label: "Status",
      render: (inv) => <StatusBadge status={inv.status} />,
    },
  ];

  return (
    <AppPage
      disablePaper
      header={
        <PageHeader
          icon={<ReceiptLongRoundedIcon />}
          title="Invoices"
          subtitle="View and download your billing invoices"
        />
      }
    >
      <DataTable
        columns={columns}
        rows={rows}
        loading={isLoading}
        actions={(inv) => (
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() => downloadPdf(inv.id, inv.invoice_number)}
              sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600 }}
            >
              PDF
            </Button>
            {inv.status !== "paid" && (
              <Button
                size="small"
                variant="contained"
                startIcon={<PaymentRoundedIcon />}
                onClick={() => pay(inv.id)}
                sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600 }}
              >
                Pay
              </Button>
            )}
          </Stack>
        )}
        empty={{
          icon: <ReceiptLongRoundedIcon />,
          title: "No invoices yet",
          message: "Your invoices will appear here once your subscription generates them.",
        }}
      />
    </AppPage>
  );
};

export default Invoices;
